import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, RadioCard, TextField } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { createSource, type ListDetail, type Source } from '../api';
import { Eyebrow } from '../ui';
import styles from './lists.module.css';

/**
 * Terminierung 07 — „Quellen" of a campaign list (webhook/API paths with the inflow today, in seven days and
 * the last lead; the URL can be copied) and the „Live-Zulauf" note. „Quelle hinzufügen" has no frame.
 */
export function SourcesCard({ list, canManage }: { list: ListDetail; canManage: boolean }) {
  const [adding, setAdding] = useState(false);
  const sources = list.campaign?.sources ?? [];
  return (
    <section className={styles.sideCard} aria-label="Quellen">
      <Eyebrow>Quellen</Eyebrow>
      <div className={styles.sources}>
        {sources.length === 0 ? <p className={styles.dialogHint}>Noch keine Quelle angebunden.</p> : null}
        {sources.map((source) => (
          <SourceRow key={source.id} source={source} />
        ))}
      </div>
      {canManage && list.campaign ? (
        <button
          type="button"
          className={clsx(styles.addCondition, styles.addSource)}
          onClick={() => setAdding(true)}
        >
          Quelle hinzufügen
        </button>
      ) : null}
      <div className={styles.liveBox}>
        <Eyebrow tone="accent">Live-Zulauf</Eyebrow>
        <p className={styles.liveText}>
          Neue Leads landen mit Status „Neu“ in der Liste und rutschen in die laufende Queue — vor den
          Wiedervorlagen, wenn „neueste Leads zuerst“ aktiv ist.
        </p>
      </div>
      {list.campaign ? (
        <AddSourceDialog open={adding} campaignId={list.campaign.id} onClose={() => setAdding(false)} />
      ) : null}
    </section>
  );
}

function SourceRow({ source }: { source: Source }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!source.url) return;
    await navigator.clipboard.writeText(source.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div className={styles.source}>
      <div className={styles.sourceHead}>
        <span className={styles.sourceName}>{source.name}</span>
        <span className={clsx(styles.sourceStatus, !source.active && styles.sourceStatusOff)}>
          {source.active ? 'aktiv' : 'pausiert'}
        </span>
      </div>
      <div className={styles.sourcePath}>
        <span>{source.label}</span>
        {source.url ? (
          <button
            type="button"
            className={styles.copy}
            aria-label={`URL von ${source.name} kopieren`}
            onClick={() => void copy()}
          >
            {copied ? 'kopiert' : 'URL kopieren'}
          </button>
        ) : null}
      </div>
      <div className={styles.sourceFigures}>
        <span>heute {formatInteger(source.today)}</span>
        <span>7 Tage {formatInteger(source.sevenDays)}</span>
        <span>letzter {source.lastLeadLabel}</span>
      </div>
    </div>
  );
}

function AddSourceDialog({
  open,
  campaignId,
  onClose,
}: {
  open: boolean;
  campaignId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState<'webhook' | 'api'>('webhook');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [created, setCreated] = useState<Source | null>(null);

  function close() {
    setName('');
    setCreated(null);
    setBanner(null);
    setNameError(undefined);
    onClose();
  }

  async function save() {
    if (name.trim() === '') {
      setNameError('Pflichtangabe.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      const source = await createSource(campaignId, { name: name.trim(), type });
      setCreated(source);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
    } catch (caught) {
      setBanner(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow="Kampagne"
      title={created ? 'Quelle angelegt' : 'Quelle hinzufügen'}
      width={560}
      footer={
        created ? (
          <Button onClick={close}>Fertig</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              Abbrechen
            </Button>
            <Button loading={pending} onClick={() => void save()}>
              Quelle anlegen
            </Button>
          </>
        )
      }
    >
      <div className={styles.dialogBody}>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {created ? (
          <>
            <p className={styles.dialogHint}>
              Leads, die an diese Adresse gesendet werden, landen mit Status „Neu“ in der Liste.
            </p>
            <div className={styles.urlBox}>{created.url ?? created.label}</div>
          </>
        ) : (
          <>
            <TextField
              label="Name"
              required
              value={name}
              error={nameError}
              onChange={(event) => {
                setName(event.target.value);
                setNameError(undefined);
              }}
            />
            <fieldset className={styles.dialogFieldset}>
              <legend className={styles.dialogLabel}>Art</legend>
              <div className={styles.typeChoices}>
                <RadioCard
                  name="dialer-source-type"
                  title="Webhook"
                  description="Formulare und Lead Ads senden die Daten"
                  checked={type === 'webhook'}
                  onChange={() => setType('webhook')}
                />
                <RadioCard
                  name="dialer-source-type"
                  title="API"
                  description="Ein anderes System übergibt die Leads"
                  checked={type === 'api'}
                  onChange={() => setType('api')}
                />
              </div>
            </fieldset>
          </>
        )}
      </div>
    </Dialog>
  );
}
